import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import {
  listBillableInstitutes,
  generateInstituteBill,
  listAllBills,
  discountBill,
  collectBill,
  deleteInstituteBill,
  extendAccessExpiry,
} from "../../api/instituteBillApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Super-admin only — the platform vendor billing an institute for using
 * digicampus (distinct from student fee invoices). Direct port of
 * digicampusbd's institute-bill generate/discount/collect/delete pages.
 */
export default function InstituteBillPage() {
  const [tab, setTab] = useState("generate");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Institute Billing (SaaS)</h1>
      </div>
      <Tabs tabs={[{ key: "generate", label: "Generate Bills" }, { key: "bills", label: "Bills" }]} active={tab} onChange={setTab} />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "generate" && <GenerateTab setError={setError} setSuccess={setSuccess} />}
      {tab === "bills" && <BillsTab setError={setError} setSuccess={setSuccess} />}
    </MainLayout>
  );
}

function GenerateTab({ setError, setSuccess }) {
  const [institutes, setInstitutes] = useState([]);
  const [selectedInstituteIds, setSelectedInstituteIds] = useState([]);
  const [months, setMonths] = useState([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [accessExpiryDate, setAccessExpiryDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listBillableInstitutes(year).then(setInstitutes);
  }, [year]);

  const toggleInstitute = (id) => setSelectedInstituteIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const toggleMonth = (m) => setMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  const toggleAllInstitutes = () =>
    setSelectedInstituteIds(selectedInstituteIds.length === institutes.length ? [] : institutes.map((i) => i.instituteId));

  const handleGenerate = async () => {
    setError("");
    if (selectedInstituteIds.length === 0 || months.length === 0) {
      setError("Select at least one institute and one month.");
      return;
    }
    setSaving(true);
    try {
      const res = await generateInstituteBill({
        instituteIds: selectedInstituteIds,
        months,
        year,
        accessExpiryDate: accessExpiryDate || undefined,
      });
      setSuccess(res.message || "Bill(s) generated.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to generate bill(s).");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Billing Year
          <input value={year} onChange={(e) => setYear(e.target.value)} style={{ width: 90 }} />
        </label>
        <label>
          New Access Expiry Date (optional)
          <input type="date" value={accessExpiryDate} onChange={(e) => setAccessExpiryDate(e.target.value)} />
        </label>
      </div>

      <div style={{ margin: "12px 0" }}>
        <strong>Months to bill:</strong>{" "}
        {MONTHS.map((m) => (
          <label key={m} className="checkbox-label" style={{ display: "inline-flex", marginRight: 10 }}>
            <input type="checkbox" checked={months.includes(m)} onChange={() => toggleMonth(m)} /> {m}
          </label>
        ))}
      </div>

      <DataTable
        columns={[
          {
            key: "select",
            label: <input type="checkbox" checked={institutes.length > 0 && selectedInstituteIds.length === institutes.length} onChange={toggleAllInstitutes} />,
            render: (i) => <input type="checkbox" checked={selectedInstituteIds.includes(i.instituteId)} onChange={() => toggleInstitute(i.instituteId)} />,
          },
          { key: "instituteName", label: "Institute" },
          { key: "contactNumber", label: "Contact" },
          { key: "billRate", label: "Rate/Student" },
          { key: "accessExpiryDate", label: "Current Access Expiry" },
        ]}
        rows={institutes}
      />

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleGenerate} disabled={saving}>
          {saving ? "Generating..." : `Generate for ${selectedInstituteIds.length} institute(s)`}
        </button>
      </div>
    </>
  );
}

function BillsTab({ setError, setSuccess }) {
  const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
  const [paymentStatus, setPaymentStatus] = useState("");
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBill, setActionBill] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [amount, setAmount] = useState("");
  const [expiryDate, setExpiryDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    listAllBills(academicYear || undefined, paymentStatus === "" ? undefined : paymentStatus === "true")
      .then(setBills)
      .catch(() => setError("Failed to load bills."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [academicYear, paymentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAction = (bill, type) => {
    setActionBill(bill);
    setActionType(type);
    setAmount("");
    setExpiryDate(today());
  };
  const closeAction = () => {
    setActionBill(null);
    setActionType(null);
  };

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill #${bill.invNo} for "${bill.instituteName}"?`)) return;
    setError("");
    try {
      await deleteInstituteBill(bill.instituteId, bill.instituteBillId);
      setSuccess("Bill deleted.");
      setBills((prev) => prev.filter((b) => b.instituteBillId !== bill.instituteBillId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete bill.");
    }
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (actionType === "discount") {
        await discountBill({ instituteId: actionBill.instituteId, instituteBillId: actionBill.instituteBillId, discountAmt: Number(amount) });
        setSuccess("Discount applied.");
      } else if (actionType === "collect") {
        await collectBill({
          instituteId: actionBill.instituteId,
          instituteBillId: actionBill.instituteBillId,
          paidAmt: Number(amount),
          accessExpiryDate: expiryDate,
        });
        setSuccess("Payment recorded.");
      } else if (actionType === "extend") {
        await extendAccessExpiry({ instituteId: actionBill.instituteId, accessExpiryDate: expiryDate });
        setSuccess("Access expiry updated.");
      }
      closeAction();
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Action failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Billing Year
          <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} style={{ width: 90 }} />
        </label>
        <label>
          Status
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
            <option value="">All</option>
            <option value="true">Paid</option>
            <option value="false">Unpaid</option>
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "instituteName", label: "Institute" },
            { key: "invNo", label: "Invoice #" },
            { key: "billMonths", label: "Months" },
            { key: "totalBill", label: "Total" },
            { key: "discountAmt", label: "Discount" },
            { key: "payableAmt", label: "Payable" },
            { key: "paidAmt", label: "Paid" },
            { key: "paymentStatus", label: "Status", render: (r) => (r.paymentStatus ? "Paid" : "Unpaid") },
          ]}
          rows={bills}
          actions={(row) => (
            <>
              <button type="button" className="btn-link" onClick={() => openAction(row, "discount")}>
                Discount
              </button>
              <button type="button" className="btn-link" onClick={() => openAction(row, "collect")}>
                Collect
              </button>
              <button type="button" className="btn-link" onClick={() => openAction(row, "extend")}>
                Extend Expiry
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {actionBill && (
        <Modal
          title={`${actionType === "discount" ? "Apply Discount" : actionType === "collect" ? "Record Payment" : "Extend Access Expiry"} — ${actionBill.instituteName}`}
          onClose={closeAction}
        >
          <form onSubmit={handleActionSubmit} className="user-form">
            {(actionType === "discount" || actionType === "collect") && (
              <>
                <label>Amount</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
              </>
            )}
            {(actionType === "collect" || actionType === "extend") && (
              <>
                <label>New Access Expiry Date</label>
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} required />
              </>
            )}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeAction}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
